import { ChangeEvent } from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Collapse from '@mui/material/Collapse'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import FormRow from 'components/FormRow'
import CurrencyField from 'components/CurrencyField/currencyField'
import ProviderPicker from 'components/ProviderPicker'
import ProductClassPicker from '../ProductClassPicker'

import { ProductClass } from '../../../../../graphql'

export type ProductFormState = {
	partId?: number,
	description?: string,
	partNumber?: string,
	provider?: any,
	upc?: string,
	standardPrice?: string,
	msrp?: string,
	isBulk?: boolean,
	productClass?: ProductClass,
}

export type ProductFormProps = {
	formState: ProductFormState,
	setFormState: ( update: ( prev: ProductFormState ) => ProductFormState ) => void,
	canEditListPrice: boolean,
}

export default function ProductForm( { formState, setFormState, canEditListPrice }: ProductFormProps ): JSX.Element {
	const { provider, description, partNumber, upc, standardPrice, msrp } = formState

	const setField = <K extends keyof ProductFormState>( field: K, value: ProductFormState[K] ) =>
		setFormState( ( prev ) => ( { ...prev, [ field ]: value } ) )

	const onChangeText = ( field: 'partNumber' | 'description' | 'upc' ) => ( event: ChangeEvent<HTMLInputElement> ) =>
		setField( field, event.target.value )

	return (
		<Stack spacing={2}>
			<ProviderPicker
				label="Manufacturer"
				onChange={( _, providerOption ) => setField( 'provider', providerOption )}
				placeholder="Search for a Manufacturer..."
				selectedProvider={provider}
				required
			/>
			<FormRow label="Manufacturer Number">
				<TextField
					fullWidth
					onChange={onChangeText( 'partNumber' )}
					placeholder="e.g. WX-123456"
					required
					value={partNumber ?? ''}
				/>
			</FormRow>
			<FormRow label="Manufacturer Description">
				<TextField
					fullWidth
					onChange={onChangeText( 'description' )}
					placeholder="e.g. Oil Filter; Avoid asset make/models"
					required
					value={description ?? ''}
				/>
			</FormRow>

			<Paper variant="outlined" sx={{ p: 2 }}>
				<FormControl component="fieldset" fullWidth>
					{/* A group heading, not a field label, so it shouldn't take the focus colour from the controls inside. */}
					<FormLabel component="legend" focused={false}>Bulk product</FormLabel>
					<FormControlLabel
						control={(
							<Checkbox
								checked={!!formState.isBulk}
								onChange={() => setFormState( ( prev ) => ( { ...prev, isBulk: !prev.isBulk } ) )}
							/>
						)}
						label="Used for bulk rentals"
					/>
					<Collapse in={!!formState.isBulk} unmountOnExit>
						<Box pt={1}>
							<ProductClassPicker
								selectedProductClass={formState.productClass}
								onChange={( _, productClass ) => setField( 'productClass', productClass as ProductClass | undefined )}
							/>
						</Box>
					</Collapse>
				</FormControl>
			</Paper>

			<FormRow label="UPC">
				<TextField
					fullWidth
					onChange={onChangeText( 'upc' )}
					value={upc ?? ''}
					slotProps={{ htmlInput: { maxLength: 14 } }}
				/>
			</FormRow>
			<FormRow label="List Price">
				<CurrencyField
					fullWidth
					onRawChange={( value ) => setField( 'standardPrice', value )}
					value={standardPrice ?? ''}
					disabled={!canEditListPrice}
				/>
			</FormRow>
			<FormRow label="MSRP">
				<CurrencyField
					fullWidth
					onRawChange={( value ) => setField( 'msrp', value )}
					value={msrp ?? ''}
				/>
			</FormRow>
		</Stack>
	)
}
